<script setup lang="ts">
import {computed, Ref, ref, watch} from "vue";
import {useDisplay} from "vuetify";
import {Project} from "@/types";
import axios from "axios";
import {route} from "ziggy-js";
import {router, usePage} from "@inertiajs/vue3";

interface Props {
    modelValue: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['update:modelValue'])
const value = computed({
    get() {
        return props.modelValue
    },
    set(value) {
        emit('update:modelValue', value)
    }
})

watch(value, (value) => {
    if (value) {
        search.value = page.props.search as string || ''
        query('owner')
        query('member')
    }
})

const { mdAndDown } = useDisplay()
const page = usePage()

const search = ref('')
const projectsAsOwner: Ref<Array<Project>> = ref([])
const projectsAsMember: Ref<Array<Project>> = ref([])

const query = (role: 'owner' | 'member') => {
    axios.get(route('web_api.projects.recent.role', {role: role}))
        .then(response => {
            if (role === 'owner') {
                projectsAsOwner.value = response.data
            } else {
                projectsAsMember.value = response.data
            }
        })
        .catch(() => {
            projectsAsOwner.value = []
            projectsAsMember.value = []
        })
}

const goToProject = (slug: string) => {
    router.visit(route('projects.show', {project: slug}), {
        onSuccess: () => {
            value.value = false
        }
    })
}

const searchPattern = () => {
    router.visit(route('search', {
        _query: {
            category: page.props.type,
            q: search.value,
        }
    }), {
        preserveState: true,
        onSuccess: () => {
            value.value = false
        }
    })
}
</script>

<template>
    <v-dialog
        v-model="value"
    >
        <v-row
            class="align-start fill-height"
            justify="center"
        >
            <v-card
                v-click-outside="() => value = false"
                :width="mdAndDown ? '100%' : '70%'"
                rounded="lg"
                :ripple="false"
            >
                <v-card-title>
                    <v-text-field
                        v-model="search"
                        :single-line="true"
                        variant="outlined"
                        density="compact"
                        :hide-details="true"
                        rounded="lg"
                        color="primary"
                        prepend-inner-icon="mdi-magnify"
                        class="mt-2"
                        :clearable="true"
                        :autofocus="true"
                        @keydown.enter="searchPattern"
                    />
                </v-card-title>
                <v-card-text
                    class="overflow-auto px-0"
                    style="max-height: 400px;"
                >
                    <v-list
                        :slim="true"
                        density="compact"
                    >
                        <v-list-item
                            v-if="search && search.length > 0"
                            :slim="true"
                            density="compact"
                            prepend-icon="mdi-magnify"
                            :link="true"
                            class="mb-2 mx-2 text-body-2"
                            rounded="lg"
                            @click="searchPattern"
                        >
                            {{ search }}
                            <template
                                #append
                            >
                                <span class="text-grey-darken-1 text-body-2">Search all of SendCrypt</span>
                            </template>
                        </v-list-item>
                        <v-divider v-if="search && search.length > 0" />
                        <v-list-subheader class="font-weight-bold mt-2 text-caption">
                            Owners
                        </v-list-subheader>
                        <v-list-item
                            v-for="project in projectsAsOwner"
                            :key="project.slug"
                            :slim="true"
                            density="compact"
                            prepend-icon="mdi-folder-outline"
                            :link="true"
                            class="mx-2 text-body-2"
                            rounded="lg"
                            @click="goToProject(project.slug)"
                        >
                            {{ project.name }}
                            <template
                                #append
                            >
                                <span class="text-grey-darken-1 text-body-2">Jump to</span>
                            </template>
                        </v-list-item>
                        <v-divider class="mt-2" />
                        <v-list-subheader class="font-weight-bold mt-2 text-caption">
                            Projects
                        </v-list-subheader>
                        <v-list-item
                            v-for="project in projectsAsMember"
                            :key="project.slug"
                            :slim="true"
                            density="compact"
                            prepend-icon="mdi-folder-outline"
                            :link="true"
                            class="mx-2 text-body-2"
                            rounded="lg"
                            @click="goToProject(project.slug)"
                        >
                            {{ project.name }}
                            <template
                                #append
                            >
                                <span class="text-grey-darken-1 text-body-2">Jump to</span>
                            </template>
                        </v-list-item>
                    </v-list>
                </v-card-text>
                <v-card-actions
                    class="border-t-sm px-5 py-3 mt-2"
                    style="min-height: 36px !important;"
                >
                    <v-spacer />
                    <div
                        class="text-none text-caption text-primary hover-underline"
                    >
                        Get help
                    </div>
                </v-card-actions>
            </v-card>
        </v-row>
    </v-dialog>
</template>

<style scoped>
:deep(.v-overlay__content) {
    top: 0;
}

.hover-underline:hover {
    text-decoration: underline;
    cursor: pointer;
}
</style>
